import { useRouteParams } from "./useRouteParams";
import { createGetPath as cU, joinRoute as u } from "./utils";

export const notFoundUrl = "/not-found";

export const loginUrl = "/login";

export const orgLoginUrl = cU("/auth/{orgUid}/login");
export const loginUrlWithParams = (
  params: typeof orgLoginUrl.type & { username: string }
) => {
  const { username, ...rest } = params;
  return `${orgLoginUrl(rest)}?username=${username}`;
};

export const orgResetPassword = cU("/auth/{orgUid}/resetPassword");

// ------------------------------------------------------
// v1
// ------------------------------------------------------

export const orgV1Url = cU("/v1/{orgUid}");
export type OrgV1UrlParams = typeof orgV1Url.type;
export const useOrgV1UrlParams = useRouteParams<OrgV1UrlParams>;

export const monitoringV1Url = cU("/v1/{orgUid}/monitoring");

// ------ v1/{orgUid}/apps [start]
export const appsV1Url = cU("/v1/{orgUid}/apps");

export const addAppV1Url = cU("/v1/{orgUid}/apps/add/{appUid}");

export const installedAppV1Url = cU("/v1/{orgUid}/apps/{installedAppUid}");

export const installedAppAgentsPageV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/agent"
);

export const installedAppAgentRuntimeV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/agent/{flowUid}"
);

export const installedAppAgentTaskV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/agent/tasks/{taskUid}"
);

export const installedAppAgentTaskFlowV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/agent/tasks/{taskUid}/flows/{flowUid}"
);

export const installedAppDeployV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/deploy"
);

export const installedAppBHScheduleUrl = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/businesshour/schedule"
);

export const installedAppEventsV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/events"
);

export const installedAppAgentRuntimeLogsV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/exec-logs"
);

export const installedAppSettingsV1Url = cU(
  "/v1/{orgUid}/apps/{installedAppUid}/settings"
);

// ------ v1/{orgUid}/apps [end]

// ------ v1/{orgUid}/data [start]
export const dataV1Url = cU("/v1/{orgUid}/data");

export const dataInstalledAppV1Url = cU("/v1/{orgUid}/data/{installedAppUid}");

export const dataFolderV1Url = cU(
  "/v1/{orgUid}/data/{installedAppUid}/folder/{folderUid}"
);

export const dataDocumentV1Url = cU(
  "/v1/{orgUid}/data/{installedAppUid}/doc/{documentUid}"
);

export const dataTableV1Url = cU(
  "/v1/{orgUid}/data/{installedAppUid}/table/{documentUid}"
);

export const dataBlobV1Url = cU(
  "/v1/{orgUid}/data/{installedAppUid}/blob/{blobUid}"
);

// ------ v1/{orgUid}/data [end]

// ------ v1/{orgUid}/insights [start]
export const insightsV1Url = cU("/v1/{orgUid}/insights");

export const insightsInstalledAppV1Url = cU(
  "/v1/{orgUid}/insights/{installedAppUid}"
);
export type InsightsInstalledAppV1UrlParams =
  typeof insightsInstalledAppV1Url.type;

export const insightsInstalledAppTagsV1Url = cU(
  "/v1/{orgUid}/insights/{installedAppUid}/tags"
);

export const insightsInstalledAppInsightV1Url = cU(
  "/v1/{orgUid}/insights/{installedAppUid}/{insightType}"
);

export const insightsInstalledAppTopXTaxonomyV1Url = cU(
  "/v1/{orgUid}/insights/{installedAppUid}/top-x/{taxonomyUid}"
);
export type InsightsInstalledAppTopXTaxonomyV1UrlParams =
  typeof insightsInstalledAppTopXTaxonomyV1Url.type;

export const taxonomyListV1Url = cU("/v1/{orgUid}/insights/taxonomy");

export const taxonomyDetailV1Url = cU(
  "/v1/{orgUid}/insights/taxonomy/{taxonomyUid}"
);

export const dataLabelDetailV1Url = cU(
  "/v1/{orgUid}/insights/taxonomy/{taxonomyUid}/data-label/{dataLabelUid}"
);
export type DataLabelDetailV1UrlParams = typeof dataLabelDetailV1Url.type;

// ------ v1/{orgUid}/insights [end]

// ------ v1/{orgUid}/settings [start]

export const settingsV1Url = cU("/v1/{orgUid}/settings");

export const accountsV1Url = cU("/v1/{orgUid}/settings/accounts");

export const apiKeysV1Url = cU("/v1/{orgUid}/settings/apikeys");

export const invitationsV1Url = cU("/v1/{orgUid}/settings/invitations");

export const secretsV1Url = cU("/v1/{orgUid}/settings/secrets");

export const billingV1Url = cU("/v1/{orgUid}/settings/billing");

export const solutionsV1Url = cU("/v1/{orgUid}/settings/solutions");

export const agentsV1Url = cU("/v1/{orgUid}/settings/agents");

// ------ v1/{orgUid}/settings [end]

// ------ v1/{orgUid}/messaging-console [start]

export const messagingConsoleV1Url = cU("/v1/{orgUid}/messaging-console");

export const messagingConsoleInstalledAppV1Url = cU(
  "/v1/{orgUid}/messaging-console/{installedAppUid}"
);

export const messagingConsoleConversationV1Url = cU(
  "/v1/{orgUid}/messaging-console/{installedAppUid}/{conversationUid}"
);
// ------ v1/{orgUid}/messaging-console [end]

export const aiSearchV1Url = cU("/v1/{orgUid}/search");

export const templateLibraryV1Url = cU(
  "/v1/{orgUid}/template-library/{templateType}"
);

// ============================================================
// org/:orgUid
// ============================================================
export interface OrgUrlParams {
  orgUid: string;
}
export const orgUrl = u<OrgUrlParams>(({ orgUid }) => `/org/${orgUid}`);
export const useOrgUrlParams = useRouteParams<OrgUrlParams>;

export const appsUrl = u(orgUrl, "apps");
export const accountsUrl = u(orgUrl, "accounts");
export const apiKeysUrl = u(orgUrl, "apikeys");
export const secretsUrl = u(orgUrl, "secrets");
export const dataPlatformUrl = u(orgUrl, "data-platform");
export const workflowOrgUrl = u(orgUrl, "workflow");

// ------------------------------------------------------
// org/add/:appUid
// ------------------------------------------------------
export interface AddAppUrlParams extends OrgUrlParams {
  appUid: string;
}
export const useAddAppUrlParams = useRouteParams<AddAppUrlParams>;
export const addAppUrl = u<AddAppUrlParams>(
  appsUrl,
  ({ appUid }) => `add/${appUid}`
);

// ------------------------------------------------------
// apps/:installedAppUid
// ------------------------------------------------------
export interface InstalledAppUrlParams extends OrgUrlParams {
  installedAppUid: string;
}
export const useInstalledAppUrlParams = useRouteParams<InstalledAppUrlParams>;

export const installedAppUrl = u<InstalledAppUrlParams>(
  appsUrl,
  ({ installedAppUid }) => installedAppUid
);

// apps/:installedAppUid/conversations
export const installedAppConversationsUrl = u<InstalledAppUrlParams>(
  installedAppUrl,
  "conversations"
);

// apps/:installedAppUid/conversations/:conversationUid
export interface InstalledAppConversationUrlParams
  extends InstalledAppUrlParams {
  conversationUid: string;
}
export const useInstalledAppConversationUrlParams =
  useRouteParams<InstalledAppConversationUrlParams>;
export const installedAppConversationUrl = u<InstalledAppConversationUrlParams>(
  installedAppConversationsUrl,
  ({ conversationUid }) => conversationUid
);

// apps/:installedAppUid/files
export const installedAppFilesUrl = u<InstalledAppUrlParams>(
  installedAppUrl,
  "files"
);

export const {
  folderUrl: installedAppFolderUrl,
  documentUrl: installedAppDocumentUrl,
  tableUrl: installedAppTableUrl,
} = createFilesUrls(installedAppFilesUrl);
export type InstalledAppFolderUrlParams = Parameters<
  typeof installedAppFolderUrl
>[0];
export type InstalledAppDocumentUrlParams = Parameters<
  typeof installedAppDocumentUrl
>[0];
export type InstalledAppTableUrlParams = Parameters<
  typeof installedAppTableUrl
>[0];

// apps/:installedAppUid/settings
export const installedAppSettingsUrl = u<InstalledAppUrlParams>(
  installedAppUrl,
  "settings"
);

// apps/:installedAppUid/settings/tags
export const installedAppSettingsTagsUrl = u<InstalledAppUrlParams>(
  installedAppSettingsUrl,
  "tags"
);

// apps/:installedAppUid/insight
export const installedAppInsightUrl = u<InstalledAppUrlParams>(
  installedAppUrl,
  "insight"
);

// apps/:installedAppUid/insight/statistics
export const installedAppStatisticsUrl = u<InstalledAppUrlParams>(
  installedAppInsightUrl,
  "statistics"
);

// apps/:installedAppUid/insight/statistics/:statisticsType
export interface InstalledAppStatisticsUrlParams extends InstalledAppUrlParams {
  statisticsType: string;
}
export const useInstalledAppStatisticsUrlParams =
  useRouteParams<InstalledAppStatisticsUrlParams>;

export const installedAppStatisticsTypeUrl = u<InstalledAppStatisticsUrlParams>(
  installedAppStatisticsUrl,
  ({ statisticsType }) => statisticsType
);

// apps/:installedAppUid/insight/analyses
export const installedAppAnalysesUrl = u<InstalledAppUrlParams>(
  installedAppInsightUrl,
  "analyses"
);

// apps/:installedAppUid/insight/analyses/:analysesType
export interface InstalledAppAnalysesUrlParams extends InstalledAppUrlParams {
  analysisType: string;
}
export const useInstalledAppAnalysesUrlParams =
  useRouteParams<InstalledAppAnalysesUrlParams>;

export const installedAppAnalysesTypeUrl = u<InstalledAppAnalysesUrlParams>(
  installedAppAnalysesUrl,
  ({ analysisType }) => analysisType
);

// ------------------------------------------------------
// createFilesUrls
// ------------------------------------------------------
export interface FilesFolderUrlParams extends OrgUrlParams {
  folderUid: string;
}
export interface FilesDocumentUrlParams extends OrgUrlParams {
  documentUid: string;
}
export interface FilesTableUrlParams extends OrgUrlParams {
  documentUid: string;
}

export function createFilesUrls<T>(baseUrl: (params: T) => string) {
  return {
    folderUrl: u<T & FilesFolderUrlParams>(
      baseUrl,
      ({ folderUid }) => `folder/${folderUid}`
    ),

    documentUrl: u<T & FilesDocumentUrlParams>(
      baseUrl,
      ({ documentUid }) => `doc/${documentUid}`
    ),

    tableUrl: u<T & FilesTableUrlParams>(
      baseUrl,
      ({ documentUid }) => `table/${documentUid}`
    ),
  };
}

// ------------------------------------------------------
// datal-platform/...
// ------------------------------------------------------

export const {
  folderUrl: dataPlatformFolderUrl,
  documentUrl: dataPlatformDocumentUrl,
  tableUrl: dataPlatformTableUrl,
} = createFilesUrls(dataPlatformUrl);

export type DataPlatformFolderUrlParams = Parameters<
  typeof dataPlatformFolderUrl
>[0];
export type DataPlatformDocumentUrlParams = Parameters<
  typeof dataPlatformDocumentUrl
>[0];
export type DataPlatformTableUrlParams = Parameters<
  typeof dataPlatformTableUrl
>[0];

export function keepCurrentSearchParams(url: string) {
  return url + window.location.search;
}

// ------------------------------------------------------
// auth/...
// ------------------------------------------------------
export const signUpUrl = (orgUid: string, invitationUid: string) => {
  return `/auth/signUp?orgUid=${orgUid}&code=${invitationUid}`;
};
